import React, { FC, MouseEvent } from 'react'
import { cn } from 'design-system'

import { Link } from 'src/lib/router'
import { gqlDisplayName } from 'shared/lib/user'

import { useIsUserOnline } from 'src/hooks/use-is-user-online'

import { AvatarUser, AvatarButtonType } from 'src/queryTypes/userTypes'
import AvatarIcon from 'src/components/user/v2/avatar-icon'

interface AvatarProps {
  user: AvatarUser
  children?: React.ReactNode
  link?: boolean
  buttonProps?: AvatarButtonType
  onClick?: (e?: MouseEvent<HTMLElement>) => void
  size?: string
  pointerEvents?: boolean
  dim?: boolean
  className?: string
  useOriginalSrc?: boolean
  tooltip?: string
  admin?: boolean
  label?: string
}

const Avatar: FC<AvatarProps> = ({
  children,
  user,
  link = false,
  tooltip,
  buttonProps,
  onClick,
  size,
  useOriginalSrc,
  pointerEvents = true,
  admin = false,
  dim = false,
  label,
  className
}) => {
  const { id } = user || {}

  const isUserOnline = useIsUserOnline(id)
  const route = id ? `/user/${id}` : ''
  const isButton = !!onClick || !!buttonProps || link

  const displayName = gqlDisplayName(user)

  const avatarInner = (
    <AvatarIcon
      user={user}
      size={size}
      useOriginalSrc={useOriginalSrc}
      component={(isButton && 'button') || 'span'}
      label={tooltip || label || displayName}
      onClick={onClick}
      hasTooltip={!!tooltip}
      buttonProps={buttonProps}
      isOnline={isUserOnline}
      isAdmin={admin}>
      {children}
    </AvatarIcon>
  )

  return (
    <div
      className={cn(
        'yd-user-avatar inline-block',
        {
          'cursor-pointer transition-opacity hover:opacity-50 focus:opacity-50 active:opacity-80':
            dim,
          'yd-user-avatar--admin': admin,
          'yd-user-avatar--small': size == 'small',
          'pointer-events-none': !pointerEvents
        },
        className
      )}>
      {link ? (
        <Link href={route} aria-label={`View ${displayName} profile`}>
          <AvatarIcon
            user={user}
            size={size}
            useOriginalSrc={useOriginalSrc}
            component="span"
            label={tooltip || label || displayName}
            onClick={onClick}
            hasTooltip={!!tooltip}
            buttonProps={buttonProps}
            isOnline={isUserOnline}
            isAdmin={admin}
            aria-hidden="true">
            {children}
          </AvatarIcon>
        </Link>
      ) : (
        avatarInner
      )}
    </div>
  )
}

export default Avatar
